import { useOidcUser } from '@axa-fr/react-oidc';
import { Badge } from '@mantine/core';
import { LoadingAnimation } from '@uag/react-core';
import { OnboardingRequestDetailModel, RequestType } from '@uag/welcome-shared/src/api/models';
import { useGetRequests } from '@uag/welcome-shared/src/api/queries/requests/requests';
import { getCustomerBadgeVariant } from '@uag/welcome-shared/src/utils/getBadgeVariant';
import { unescapeUnicode } from '@uag/welcome-shared/src/utils/unicodeUtils';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import TimeAgo from 'timeago-react';

export const ManageRequests = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { data: requestsResponse, isLoading: isRequestsLoading } = useGetRequests({
        requestTypes: [RequestType.NonDoka360],
    });
    const { oidcUser } = useOidcUser();
    const requests = requestsResponse?.data;

    if (isRequestsLoading) {
        return <LoadingAnimation />;
    }

    const handleClick = (requestId: string, request?: OnboardingRequestDetailModel) => {
        if (requestId === 'newRequest') {
            return navigate('/requests/create');
        }
        if (request?.currentState === 'Expired') {
            return navigate('/requests/expired');
        }
        navigate(`/requests/${requestId}`);
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col">
                <h1 className="text-4xl md:p-0">{t('welcomeBack')}</h1>
                <p className="text-text-light text-base">{t('whichCompanyRegistration')}</p>
            </div>
            <div className="flex flex-col gap-4">
                {requests?.map((request) => (
                    <button
                        key={request.id}
                        className="card card-clickable w-full p-4 shadow-none"
                        value={request.id}
                        onClick={() => handleClick(request.id, request)}
                    >
                        <div className="gap-base flex w-full justify-between p-2">
                            <div className="flex-col text-base">
                                <div className="flex justify-start">
                                    {request.companyData?.companyName && (
                                        <h6 className="font-base text-left">
                                            {unescapeUnicode(request.companyData?.companyName)}
                                        </h6>
                                    )}
                                </div>
                                <div className="flex items-center gap-1">
                                    <div className="text-text-light flex gap-1 text-sm">
                                        {request.currentState && (
                                            <Badge variant={getCustomerBadgeVariant(request.currentState)}>
                                                {t(`customerRequestState.${request.currentState}`)}
                                            </Badge>
                                        )}
                                        {request.currentState && request.modifiedDate && <span>|</span>}
                                        {request.modifiedDate && (
                                            <span>
                                                <TimeAgo datetime={request.modifiedDate} locale={oidcUser.locale} />
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="flex justify-end">
                                <span className="text-text-ultra-light self-start text-xs">{`${t('createdOn')} ${new Date(request.createdDate).toLocaleDateString(oidcUser.locale)}`}</span>
                            </div>
                        </div>
                    </button>
                ))}
            </div>
            <div className="flex justify-center gap-1">
                <p className="text-base">{t('otherCompany')}</p>
                <button className="text-text-accent text-base" onClick={() => handleClick('newRequest')}>
                    {t('startNewRegistration')}
                </button>
            </div>
        </div>
    );
};
